import {
  getCellByOrderAndMapId,
  getCellGroup,
  getMapById,
  getPlayerByOrder,
} from "./gameHelpers"
import { setState } from "@/store/globalSlice"
import { store } from "@/store/store"
import { socket } from "./utils"

const sum = (values: Array<number | null | undefined>): number =>
  values.reduce<number>((acc, v) => acc + (v ?? 0), 0)

/**
 * The only two slices of global state anything in here reads: the game as the
 * server last sent it, and the unsaved changes the property-management screen
 * is holding on top of it.
 *
 * Named as its own type so a caller can hand over just these two. The
 * signatures used to ask for the whole `IGlobalState`, which meant every screen
 * calling one of these had to subscribe to the entire store to satisfy it — and
 * so re-rendered on every dispatch in the app.
 */
export type PropMngState = Pick<IGlobalState, "myGame" | "tempPropMngData">

export const canPutHousesOnPropertyGroup = (
  state: PropMngState,
  playerOrder: number,
  groupId: string
) => {
  const game = state.myGame as IGameDetails
  const map = getMapById(game.settings.mapId) as IMapDesign
  const group = map.groups.find((group) => group.id === groupId)

  if (!group?.withStrip) return false
  const player = getPlayerByOrder(game, playerOrder)
  if (!player) throw new Error("Player not found")
  const propertiesOfThisGroup = map?.cells.filter(
    (cell) => cell.propertyDetails?.groupId === groupId
  )
  const allPropertiesOwned = propertiesOfThisGroup.every((prop) =>
    player.properties.includes(prop.order)
  )
  const noPropertyMortgaged = propertiesOfThisGroup.every(
    (prop) => !state.tempPropMngData?.mortgagedProperties.includes(prop.order)
  )
  return allPropertiesOwned && noPropertyMortgaged
}

export const getHouseDataByPlayerOrder = (
  state: PropMngState,
  playerOrder: number
) => {
  const player = getPlayerByOrder(state.myGame as IGameDetails, playerOrder)
  const properties = player?.properties
  return state.tempPropMngData.houseData.filter((item) =>
    properties?.includes(item.propertyOrder)
  )
}

type CheckFunction = (
  item: PlayerPropertyHouseData,
  index: number,
  thisPropertyHouseData: PlayerPropertyHouseData
) => boolean
export const checkForSimilarGroupPropertiesOfPlayer = (
  state: PropMngState,
  playerOrder: PlayerOrder,
  propertyOrder: number,
  checkFunction: CheckFunction
) => {
  const game = state.myGame as IGameDetails
  const property = getCellByOrderAndMapId(
    propertyOrder,
    game?.settings?.mapId || ""
  )

  const map = getMapById(game.settings.mapId) as IMapDesign
  const propertiesOfThisGroup = map?.cells
    .filter(
      (cell) =>
        cell.propertyDetails?.groupId === property?.propertyDetails?.groupId
    )
    .map((property) => property.order)
  const playerPropertiesHouseData = getHouseDataByPlayerOrder(
    state,
    playerOrder
  )
  const thisPropertyHouseData = playerPropertiesHouseData?.find(
    (item) => item.propertyOrder === propertyOrder
  ) as PlayerPropertyHouseData
  const houseDataOfThisPlayerAndGroup = playerPropertiesHouseData.filter(
    (houseData) => propertiesOfThisGroup.includes(houseData.propertyOrder)
  )
  const noOfHousesEqualOrGreater = houseDataOfThisPlayerAndGroup?.every(
    (item, index) => checkFunction(item, index, thisPropertyHouseData)
  )
  return noOfHousesEqualOrGreater
}
export const canRemoveLastHouse = (
  state: PropMngState,
  playerOrder: PlayerOrder,
  propertyOrder: number
) =>
  checkForSimilarGroupPropertiesOfPlayer(
    state,
    playerOrder,
    propertyOrder,
    (item, _index, thisPropertyHouseData) =>
      item.houses <= thisPropertyHouseData!.houses
  )

export const canPutNewHouseOnProperty = (
  state: PropMngState,
  playerOrder: PlayerOrder,
  propertyOrder: number
) =>
  checkForSimilarGroupPropertiesOfPlayer(
    state,
    playerOrder,
    propertyOrder,
    (item, _index, thisPropertyHouseData) =>
      item.houses >= thisPropertyHouseData!.houses
  )

export const getPropChangesAfterPropManagement = (
  state: PropMngState
): IPropManagementChange => {
  const tempPropMngData = state.tempPropMngData
  const prevHouseData = state.myGame?.situation?.housesData
  const mortgagedProps = state.myGame?.situation?.mortgagedProperties
  const newlyMortgagedProps = tempPropMngData.mortgagedProperties.filter(
    (prop) => !mortgagedProps?.includes(prop)
  )
  const newlyUnMorgagedProps = mortgagedProps?.filter(
    (prop) => !tempPropMngData.mortgagedProperties.includes(prop)
  )
  const houseChanges = prevHouseData?.map((item) => {
    const currentData = tempPropMngData.houseData.find(
      (itemInner) => itemInner.propertyOrder === item.propertyOrder
    ) as PlayerPropertyHouseData
    return { ...item, houses: currentData?.houses - item.houses }
  })
  return {
    mortgagedProps: newlyMortgagedProps,
    unMortgagedProps: newlyUnMorgagedProps || [],

    houseChanges: houseChanges || [],
  }
}
const getMortgagePrice = (mapId: string, order: number) =>
  getCellByOrderAndMapId(order, mapId)?.propertyDetails?.mortgagedPrice || 0

const getUnMortgagePrice = (mapId: string, order: number) => {
  const mortgagePrice = getMortgagePrice(mapId, order)
  return mortgagePrice + mortgagePrice * 0.1
}

export const getHouseBuyPrice = (
  mapId: string,
  order: number,
  itemNo: number
): number => {
  const cell = getCellByOrderAndMapId(order, mapId) as ICell
  return cell.propertyDetails!.rentData!.upgradeItems[itemNo - 1].price
  // const group = getCellGroup(mapId, cell)
  // if (!group?.housePrice) return 0
  // return group.housePrice
}
export const getHouseSellPrice = (
  mapId: string,
  order: number,
  itemNo: number
): number => {
  const cell = getCellByOrderAndMapId(order, mapId) as ICell

  return cell.propertyDetails!.rentData!.upgradeItems[itemNo - 1].price / 2
  // const group = getCellGroup(mapId, cell)
  // if (!group?.housePrice) return 0
  // return group.housePrice / 2
}
export const getNetMoneyChangeAfterPropManagment = (state: PropMngState) => {
  const {
    moneyGainedByMortgaging,
    moneyGainedBySellingHouses,
    moneyUsedByBuyingHouses,
    moneyUsedByUnMortgaging,
  } = getMoneyChangesAfterPropManagement(state)
  return (
    moneyGainedByMortgaging +
    moneyGainedBySellingHouses -
    moneyUsedByBuyingHouses -
    moneyUsedByUnMortgaging
  )
}
export const getMoneyChangesAfterPropManagement = (state: PropMngState) => {
  const mapId = state.myGame?.settings.mapId || ""
  const changes = getPropChangesAfterPropManagement(state)
  const moneyGainedByMortgaging = sum(
    changes!.mortgagedProps?.map((item) => getMortgagePrice(mapId, item))
  )
  const moneyUsedByUnMortgaging = sum(
    changes!.unMortgagedProps?.map((item) => getUnMortgagePrice(mapId, item))
  )
  const moneyGainedBySellingHouses = sum(
    changes!.houseChanges?.map((item) => {
      if (item.houses < 0)
        return sum(
          Array(Math.abs(item.houses))
            .fill(0)
            .map((_, index) => {
              const housesData = state.myGame?.situation?.housesData.find(
                (housesData) => housesData.propertyOrder === item.propertyOrder
              )
              return getHouseSellPrice(
                mapId,
                item.propertyOrder,
                housesData!.houses - index
              )
            })
        )
      return 0
    })
  )

  const moneyUsedByBuyingHouses = sum(
    changes!.houseChanges?.map((item) => {
      if (item.houses > 0)
        return sum(
          Array(item.houses)
            .fill(0)
            .map((_, index) => {
              const housesData = state.myGame?.situation?.housesData.find(
                (housesData) => housesData.propertyOrder === item.propertyOrder
              )
              return getHouseBuyPrice(
                mapId,
                item.propertyOrder,
                (housesData?.houses || 0) + index + 1
              )
            })
        )
      return 0
    })
  )
  return {
    moneyUsedByBuyingHouses,
    moneyGainedByMortgaging,
    moneyGainedBySellingHouses,
    moneyUsedByUnMortgaging,
  }
}

// ---------------------------------------------------------------------------
// Interactive (click-on-board) house buy/sell.
//
// Unlike the staged Property Management flow above (which accumulates changes
// in tempPropMngData and applies them all at once), these helpers read the
// LIVE game situation and let the player build/sell a single house per click,
// applied immediately and finally.
// ---------------------------------------------------------------------------

const getGroupPropertyOrders = (mapId: string, groupId: string): number[] =>
  (getMapById(mapId)?.cells || [])
    .filter((cell) => cell.propertyDetails?.groupId === groupId)
    .map((cell) => cell.order)

const housesOnProperty = (game: IGameDetails, order: number): number =>
  game.situation?.housesData.find((item) => item.propertyOrder === order)
    ?.houses || 0

const getMaxHouses = (mapId: string, order: number): number =>
  getCellByOrderAndMapId(order, mapId)?.propertyDetails?.rentData?.upgradeItems
    .length || 0

// The full color group is owned by the player and none of its properties are
// mortgaged — the precondition for any building/selling on the group.
const ownsBuildableGroup = (
  game: IGameDetails,
  playerOrder: PlayerOrder,
  groupId: string
): boolean => {
  const map = getMapById(game.settings.mapId)
  const group = map?.groups.find((g) => g.id === groupId)
  if (!group?.withStrip) return false
  const player = getPlayerByOrder(game, playerOrder)
  if (!player) return false
  const groupOrders = getGroupPropertyOrders(game.settings.mapId, groupId)
  const ownsAll = groupOrders.every((o) => player.properties.includes(o))
  const noneMortgaged = groupOrders.every(
    (o) => !game.situation?.mortgagedProperties.includes(o)
  )
  return ownsAll && noneMortgaged
}

// Whether the player owns at least one full, build-eligible color group (a
// "completed set"). This is the precondition for buying any houses, regardless
// of whether a house can currently be added to a specific property.
export const playerHasCompleteSet = (
  game: IGameDetails,
  playerOrder: PlayerOrder
): boolean => {
  const map = getMapById(game.settings.mapId)
  if (!map) return false
  return (map.groups || []).some((group) =>
    ownsBuildableGroup(game, playerOrder, group.id)
  )
}

// Whether the player currently has any houses built on their properties.
export const playerHasAnyHouses = (
  game: IGameDetails,
  playerOrder: PlayerOrder
): boolean => {
  const player = getPlayerByOrder(game, playerOrder)
  if (!player) return false
  return (game.situation?.housesData || []).some(
    (item) => player.properties.includes(item.propertyOrder) && item.houses > 0
  )
}

export const canBuyHouseLive = (
  game: IGameDetails,
  playerOrder: PlayerOrder,
  propertyOrder: number
): boolean => {
  const mapId = game.settings.mapId
  const property = getCellByOrderAndMapId(propertyOrder, mapId)
  const groupId = property?.propertyDetails?.groupId
  if (!groupId) return false
  const player = getPlayerByOrder(game, playerOrder)
  if (!player || !player.properties.includes(propertyOrder)) return false
  if (!ownsBuildableGroup(game, playerOrder, groupId)) return false

  const myHouses = housesOnProperty(game, propertyOrder)
  if (myHouses >= getMaxHouses(mapId, propertyOrder)) return false

  // Even-build rule: can only add a house if this property doesn't already
  // have more houses than any other property in the group.
  const groupOrders = getGroupPropertyOrders(mapId, groupId)
  const evenBuildOk = groupOrders.every(
    (o) => myHouses <= housesOnProperty(game, o)
  )
  if (!evenBuildOk) return false

  // Note: affordability is intentionally NOT checked here, so a property the
  // player can legally build on stays highlighted even when they can't afford
  // the next house. The insufficient-funds case is handled on click (with a
  // notification) — see canAffordNextHouse / the build click handler.
  return true
}

// Whether the player can pay for the next house on this property. Used at click
// time to show a notification instead of silently doing nothing.
export const canAffordNextHouse = (
  game: IGameDetails,
  playerOrder: PlayerOrder,
  propertyOrder: number
): boolean => {
  const player = getPlayerByOrder(game, playerOrder)
  if (!player) return false
  const current = housesOnProperty(game, propertyOrder)
  const price = getHouseBuyPrice(game.settings.mapId, propertyOrder, current + 1)
  return player.money >= price
}

export const canSellHouseLive = (
  game: IGameDetails,
  playerOrder: PlayerOrder,
  propertyOrder: number
): boolean => {
  const mapId = game.settings.mapId
  const property = getCellByOrderAndMapId(propertyOrder, mapId)
  const groupId = property?.propertyDetails?.groupId
  if (!groupId) return false
  const player = getPlayerByOrder(game, playerOrder)
  if (!player || !player.properties.includes(propertyOrder)) return false

  const myHouses = housesOnProperty(game, propertyOrder)
  if (myHouses <= 0) return false

  // Even-build rule: can only remove a house if this property doesn't already
  // have fewer houses than any other property in the group.
  const groupOrders = getGroupPropertyOrders(mapId, groupId)
  const evenSellOk = groupOrders.every(
    (o) => myHouses >= housesOnProperty(game, o)
  )
  return evenSellOk
}

// Apply a single house change (build = +1, sell = -1) to one property and emit
// it to the server immediately. Reuses the same propmng.apply-changes pipeline
// as the staged flow, so animations and money updates behave identically.
//
// The house count and the balance are also updated locally straight away.
// Nothing in myGame changes until the server echoes the new situation back, so
// without that the board keeps showing the old count and the old money for a
// full round trip — long enough to click the same property again and be priced
// off pre-click state, or to click a second property in the group and have both
// clicks read the same stale counts. The server applies changes as deltas and
// re-prices them, so the echo is what settles the real numbers; this only
// closes the window where the board lies about what has already been spent.
export const emitImmediateHouseChange = (
  state: PropMngState,
  propertyOrder: number,
  delta: 1 | -1
) => {
  const game = state.myGame
  if (!game?.situation) return
  const mapId = game.settings.mapId
  const housesData = game.situation.housesData
  const current = housesOnProperty(game, propertyOrder)
  const newHouses = current + delta

  const newHouseData = housesData.map((item) =>
    item.propertyOrder === propertyOrder
      ? { ...item, houses: newHouses }
      : item
  )

  const changes: IPropManagementChange = {
    mortgagedProps: [],
    unMortgagedProps: [],
    houseChanges: [{ propertyOrder, houses: delta }],
  }

  const netMoneyChange =
    delta > 0
      ? -getHouseBuyPrice(mapId, propertyOrder, newHouses)
      : getHouseSellPrice(mapId, propertyOrder, current)

  socket.emit("propmng.apply-changes", game.id, changes, netMoneyChange, {
    houseData: newHouseData,
    mortgagedProperties: game.situation.mortgagedProperties,
  })

  store.dispatch(
    setState({
      myGame: {
        ...game,
        situation: {
          ...game.situation,
          housesData: newHouseData,
          players: game.situation.players.map((player) =>
            player.id === game.situation!.turn
              ? { ...player, money: player.money + netMoneyChange }
              : player
          ),
        },
      },
    })
  )
}
